// The auto-send guards G1–G8 (BUILD 2 C2-8), pure. Every input is structural:
// who committed, a word count, the draft's state, the provider's `degraded`
// and `endpointVerified`, the focus card, page visibility, the thread and the
// session. Nothing here reads an utterance's text.
//
// `commitOutcome` runs when a turn is committed and decides what the
// utterance becomes; `sendOutcome` runs when the undo window ends (or on Send
// now) and decides whether it is sent. No chat request is ever made inside
// the undo window; the controller owns that.
import type { VoiceCommit, VoiceFocusCard } from './contracts';
import { ENDPOINT_MIN_WORDS } from './policy';

/**
 * What a committed utterance becomes:
 *   send     — the undo countdown starts, then `sendOutcome` decides;
 *   pending  — a pending bubble that needs a tap to send;
 *   insert   — inserted in the composer at the caret, never sent (G2, G3);
 *   draft    — kept as a draft, never sent without a user action (G1, G6).
 */
export type CommitOutcome = 'send' | 'pending' | 'insert' | 'draft';
/** The guard that decided, or null when every guard held. */
export type GuardId = 'G1' | 'G2' | 'G3' | 'G4' | 'G5' | 'G6' | 'G7' | 'G8';
export type Decision<T> = { outcome: T; guard: GuardId | null };

const decide = <T>(outcome: T, guard: GuardId | null = null): Decision<T> => ({ outcome, guard });

export type CommitInput = {
  /** G1: voice mode is on in this tab. */
  voiceOn: boolean;
  /** G1: the company's `speech_policy.voiceMode`. */
  policyAllows: boolean;
  /** G2: what committed the turn. */
  commit: VoiceCommit;
  /** G2: the provider's word count (an endpoint's `words`), else a segmenter count. */
  words: number;
  /** G2: hands-free is available for this person now. */
  handsFreeAvailable: boolean;
  /** G2: the committed stream provider supports semantic endpointing. */
  endpointVerified: boolean;
  /** G3: the draft was empty, with no attachment, when the capture began. */
  draftEmptyAtStart: boolean;
  /** G3: something was typed or attached while capturing. */
  draftChanged: boolean;
  /** G4: the committed text is empty. */
  empty: boolean;
  /** G4: the provider degraded (to a backup engine, or to utterance mode). */
  degraded: boolean;
  /** G5: the focus card, if any. */
  focus: VoiceFocusCard | null;
  /** G5: the latest assistant message, which a focus card must come from. */
  latestAssistantMessageId: string | null;
  /** G6: the page is visible. */
  visible: boolean;
  /** G6: the browser is online. */
  online: boolean;
};

/** G5: a card created by the latest assistant message that this user can act on now. */
export const holdsAutoSend = (
  focus: VoiceFocusCard | null,
  latestAssistantMessageId: string | null,
): boolean =>
  Boolean(focus?.actionable) &&
  latestAssistantMessageId !== null &&
  focus?.messageId === latestAssistantMessageId;

/** G2: a tap commits directly; an endpoint needs one provider-recognized word. */
function g2(input: CommitInput): boolean {
  if (input.commit !== 'endpoint') return true;
  return input.handsFreeAvailable && input.words >= ENDPOINT_MIN_WORDS;
}

/** What a committed utterance becomes (see the header). Checked in order G1 … G6. */
export function commitOutcome(input: CommitInput): Decision<CommitOutcome> {
  if (!input.voiceOn || !input.policyAllows) return decide('draft', 'G1');
  if (!g2(input)) return decide('insert', 'G2');
  if (!input.draftEmptyAtStart || input.draftChanged) return decide('insert', 'G3');
  if (input.empty || input.degraded) return decide('pending', 'G4');
  if (holdsAutoSend(input.focus, input.latestAssistantMessageId)) return decide('pending', 'G5');
  if (!input.visible || !input.online) return decide('draft', 'G6');
  // A semantic endpoint must come from a provider that declares that capability.
  if (input.commit === 'endpoint' && !input.endpointVerified) return decide('pending', 'G2');
  return decide('send');
}

export type SendOutcome = 'send' | 'cancel_then_send' | 'draft';

export type SendInput = {
  /** G6: the page is visible and online, and the question gate allows a turn. */
  visible: boolean;
  online: boolean;
  gateAllows: boolean;
  /** G7: a run is in flight. */
  running: boolean;
  /** G8: the thread and the voice session are the ones the turn was committed in. */
  sameThread: boolean;
  sameSession: boolean;
  /** Send now: the person tapped Send, so G6's visibility needs no re-check. */
  explicit: boolean;
};

/** Whether the pending turn is sent at the end of the window (or on Send now). */
export function sendOutcome(input: SendInput): Decision<SendOutcome> {
  if (!input.sameThread || !input.sameSession) return decide('draft', 'G8');
  if ((!input.visible && !input.explicit) || !input.online || !input.gateAllows)
    return decide('draft', 'G6');
  if (input.running) return decide('cancel_then_send', 'G7');
  return decide('send');
}
